import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

const IMG = "https://images.unsplash.com/photo-1770062421988-7929b4748e29?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const u = await login(email, password);
      const dest = u?.role === "admin" ? "/admin" : u?.role === "creator" ? "/studio" : "/feed";
      navigate(dest);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src={IMG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/30 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10">
          <h2 className="font-display text-5xl leading-none">Welcome back to your <span className="italic text-primary">fantasy</span>.</h2>
        </div>
      </div>
      <div className="flex items-center justify-center px-6 py-16">
        <motion.form
          onSubmit={submit} data-testid="login-form"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="w-full max-w-sm"
        >
          <p className="font-accent text-xs uppercase tracking-[0.4em] text-primary mb-3">Sign in</p>
          <h1 className="font-display text-4xl mb-8">Log in</h1>
          {error && <p data-testid="login-error" className="mb-4 text-sm text-primary bg-primary/10 border border-primary/30 px-3 py-2">{error}</p>}
          <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Email</label>
          <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-card border border-border px-4 py-3 mb-5 focus:border-primary outline-none transition-colors" />
          <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Password</label>
          <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-card border border-border px-4 py-3 mb-8 focus:border-primary outline-none transition-colors" />
          <button data-testid="login-submit" disabled={busy}
            className="w-full py-4 bg-primary text-primary-foreground font-accent font-bold uppercase tracking-widest text-sm transition-transform active:scale-95 hover:bg-[#e6004c] disabled:opacity-60">
            {busy ? "Signing in…" : "Log in"}
          </button>
          <p className="mt-6 text-sm text-muted-foreground text-center">
            New here? <Link to="/register" data-testid="to-register" className="text-primary hover:underline">Create an account</Link>
          </p>
        </motion.form>
      </div>
    </div>
  );
}
